import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorHandlerService } from '@aveva/connect-web-core';
import { first, firstValueFrom } from 'rxjs';
import { Class } from 'src/app/models/class.model';
import { ClassesService } from 'src/app/services/classes.service';
import { StudentsService } from 'src/app/services/students.service';
import * as globals from '../../globals';
import { Dataset } from 'src/app/models/dataset.model';
import { DatasetsService } from 'src/app/services/datasets.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../shared/confirmation-dialog/confirmation-dialog.component';
import { ConfigurationsService } from 'src/app/services/configurations.service';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSort } from '@angular/material/sort';
import { MediaObserver } from '@angular/flex-layout';
import { Service } from 'src/app/models/service.model';
import { LoggingService } from '../../../services/logging.service';

@Component({
  standalone: false,
  selector: 'app-modify-class',
  templateUrl: './modify-class.component.html',
  styleUrls: ['./modify-class.component.scss']
})
export class ModifyClassComponent implements OnInit {

  constructor(
    private errorHandler: ErrorHandlerService,
    private classesService: ClassesService,
    private studentsService: StudentsService,
    private datasetsService: DatasetsService,
    private configurationsService: ConfigurationsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    public media: MediaObserver,
    private logger: LoggingService) { }

  ngOnInit(): void {
    this.loadDatasets();
    this.selection.clear();
    this.isContributor = ([`${globals.TC}:${globals.trainingadmin}`, `${globals.TC}:${globals.trainer}`].includes(localStorage.getItem(globals.POLICIES)));
  }
  
  public retrieveCreateServices(showLoadingSpinner: boolean) {
    try {
      if(showLoadingSpinner) { 
        this.loadingSpinner.emit(true);
      }
              
      this.configurationsService.getConnectCreateServices().subscribe({
        next: (data) => {
          this.dataSource = new MatTableDataSource(data);
          this.servicesLength = data.length;
          this.selection.clear();
          this.dataSource.data.forEach(row => (this.class?.services.includes(row.name) || this.isCheckBoxView(row) ? this.selection.select(row) : this.selection.deselect(row)));
          this.loadingSpinner.emit(false);
        },
        error: (e) => {
          this.logger.logException(e);
          this.loadingSpinner.emit(false);
        }
      });
    }
    catch (exception) {
      this.logger.logException(exception);
      if(showLoadingSpinner) { 
        this.loadingSpinner.emit(false);
      }
    }
  }

  async update() {
    try {
      this.logger.logEvent('Class Update initialised', {classId: this.class?.id});
      this.form.controls['dataset_id'].markAsUntouched();
      this.loadingSpinner.emit(true);
      
      if (this.submitting) {
        return;
      }

      const classServicesLower = this.selection.selected.map(service => service.name.toLowerCase()).join(';');
      const datasetSelected = this.datasets?.find(d => d.id === this.class.dataset_id);

      if(classServicesLower.includes('datahub') && datasetSelected?.name.toLowerCase().includes('none')) {
        this.displaySnackBar(globals.dataset_cds_select);
        this.loadingSpinner.emit(false);
        return;
      }
  
      this.class.name = this.class.name.trim();

      const isClassExists = await this.IsClassNameExists();
      if(isClassExists) {
        this.displaySnackBar(globals.className_exists);
        this.loadingSpinner.emit(false);
        return;
      }
  
      const students = await firstValueFrom(this.studentsService.getStudentsCdsInUseByClassId(this.class.id));
      const classData = await firstValueFrom(this.classesService.getClassById(this.class.id));
  
      if((students.length > 0) && (classData?.dataset_id !== this.class?.dataset_id)) {
        this.displaySnackBar(globals.cds_inuse_dataset_changed);
        this.loadingSpinner.emit(false);
        return;
      }
  
      this.submitting = true;

      this.class.services = this.selection.selected.map(service => service.name).join(';');

      const data = await firstValueFrom(this.classesService.update_class(this.class));
      this.logger.logEvent('Class Update Success', {classId: this.class?.id});
      this.form.resetForm();
      this.loadingSpinner.emit(false);
      this.displaySnackBar(globals.class_updated);
      this.saved.emit(data);
    } 
    catch (error) {
      this.logger.logException(error);
      this.displaySnackBar(globals.class_update_failed);      
      this.loadingSpinner.emit(false);
      
      if (error.status === 409) {
        this.form.controls['name'].setErrors({ 'notUnique': true });
      } else if (error.error?.statusCode === 400 && error.error?.errorCode === globals.RESOURCELIMIT) {
        this.errorHandler.handle(error.error, globals.CLASS_LIMIT_REACHED);
      } else {
        return this.errorHandler.handle(error);
      }
    }
    finally {
      this.submitting = false;
    }
    
    this.selection.clear(); // 🔥 critical
  }

  private loadDatasets() {
    try {
      this.datasetsService.get_datasets().subscribe({
        next: (data) => this.datasets = data
      });
    }
    catch {}
  }

  async delete() {
    try {
      this.logger.logEvent('Class delete initialised', {classId: this.class?.id});
      this.loadingSpinner.emit(true);
      const studentsCount = await firstValueFrom(this.studentsService.getStudentsCountByClassId(this.class.id));

      if(studentsCount > 0) {
        this.displaySnackBar(globals.class_withStudents);
        this.loadingSpinner.emit(false);
        return;
      }
      
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          classId: this.class?.id,
          dialogTitle: 'Delete Class',
          description: `${globals.DELETE_WARN} ${this.class?.name}?`,
          cautionaryMessage: '',
          okButtonTitle: globals.DELETE,
        },
        width: '500px'
      });

      const result = await firstValueFrom(dialogRef
        .afterClosed()
        .pipe(first()));

      if (result) {
        this.logger.logEvent('Class Delete Successful', {classId: this.class?.id});
        this.loadingSpinner.emit(true);
        await firstValueFrom(this.classesService.deleteClass(this.class.id));
        this.displaySnackBar(globals.class_deleted);
        this.loadingSpinner.emit(false);
        this.saved.emit();
      }
      else {
        this.loadingSpinner.emit(false);
        this.cancelled.emit();
      }
    }
    catch {
      this.loadingSpinner.emit(false);
    }
  }

  private async IsClassNameExists(): Promise<boolean> {
    try {
      const isClassNameExists = await firstValueFrom(this.classesService.isCLass_exists(this.class.id, this.class.name));
      return isClassNameExists;
    }
    finally {
      return false;      
    }
  }

  cancel = () => {
    this.cancelled.emit();
  };

  close() {
    this.form.controls['name'].markAsUntouched();
    this.cancelled.emit();
  }

  displaySnackBar = (message: string) => this.snackBar.open(message, null, {duration: 3000});

  private isCheckBoxView = (row: Service) => (row.type === 'Required');

  isContributor: boolean;
  submitting = false;
  datasets: Dataset[];
  servicesLength = 0;
  dataSource = new MatTableDataSource<Service>();
  selection = new SelectionModel<Service>(true, []);
  displayedColumns: string[] = globals.services_columns;

  @Input() class: Class = new Class();

  @Output() saved: EventEmitter<any> = new EventEmitter();
  @Output() cancelled: EventEmitter<any> = new EventEmitter();
  @Output() loadingSpinner: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('form', { static: true }) form: NgForm;
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  @HostListener('window:beforeunload')
  canDeactivate = (): boolean => !this.form.dirty || this.form.submitted;

}
